import { Controller } from "stimulus";
import { DirectUpload } from "@rails/activestorage";

export default class extends Controller {
  static targets = ["input", "dropzone", "fileList", "submit"];

  connect() {
    this.uploads = [];
    this.idCounter = 0;
    this.formEl = this.element.closest("form") || this.element;
    this.directUploadUrl = this.inputTarget.dataset.directUploadUrl;
    this.fieldName = this.inputTarget.name;

    this.inputTarget.removeAttribute("name");
    this.inputTarget.removeAttribute("required");

    this.refreshSubmitState();
  }

  filesChanged() {
    Array.from(this.inputTarget.files || []).forEach((file) => this.queueUpload(file));
    this.inputTarget.value = "";
  }

  dragOver(event) {
    event.preventDefault();
    this.dropzoneTarget.classList.add("is-dragover");
  }

  dragLeave(event) {
    event.preventDefault();
    this.dropzoneTarget.classList.remove("is-dragover");
  }

  drop(event) {
    event.preventDefault();
    this.dropzoneTarget.classList.remove("is-dragover");
    Array.from(event.dataTransfer.files).forEach((file) => this.queueUpload(file));
  }

  remove(event) {
    event.preventDefault();
    event.stopPropagation();

    const id = Number(event.currentTarget.dataset.uploadId);
    const upload = this.uploads.find((u) => u.id === id);
    if (!upload) return;

    if (upload.xhr) upload.xhr.abort();
    if (upload.hiddenInput && upload.hiddenInput.parentNode) {
      upload.hiddenInput.parentNode.removeChild(upload.hiddenInput);
    }
    if (upload.chipEl && upload.chipEl.parentNode) {
      upload.chipEl.parentNode.removeChild(upload.chipEl);
    }
    this.uploads = this.uploads.filter((u) => u.id !== id);

    if (this.uploads.length === 0) {
      this.fileListTarget.classList.add("is-empty");
    }
    this.refreshSubmitState();
  }

  queueUpload(file) {
    const id = ++this.idCounter;
    const upload = { id, file, status: "uploading", progress: 0 };
    this.uploads.push(upload);
    this.fileListTarget.classList.remove("is-empty");
    this.appendChip(upload);
    this.refreshSubmitState();

    const directUpload = new DirectUpload(file, this.directUploadUrl, {
      directUploadWillStoreFileWithXHR: (xhr) => {
        upload.xhr = xhr;
        xhr.upload.addEventListener("progress", (e) => {
          if (!e.lengthComputable) return;
          upload.progress = (e.loaded / e.total) * 100;
          this.updateChip(upload);
        });
      },
    });

    directUpload.create((error, attributes) => {
      if (error) {
        upload.status = "error";
        upload.errorMessage = typeof error === "string" ? error : "Upload failed";
        if (window.notyf) {
          window.notyf.error(`Couldn't upload "${file.name}". Try again.`);
        }
      } else {
        upload.status = "complete";
        upload.progress = 100;
        upload.signedId = attributes.signed_id;
        upload.hiddenInput = this.createHiddenInput(attributes.signed_id);
      }
      this.updateChip(upload);
      this.refreshSubmitState();
    });
  }

  createHiddenInput(signedId) {
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = this.fieldName;
    input.value = signedId;
    this.formEl.appendChild(input);
    return input;
  }

  appendChip(upload) {
    const chip = document.createElement("div");
    chip.className = "portal-uploader-chip is-uploading";

    const icon = document.createElement("div");
    icon.className = "portal-uploader-chip-icon";
    icon.innerHTML = `<i class="fa fa-${this.iconFor(upload.file)}"></i>`;

    const body = document.createElement("div");
    body.className = "portal-uploader-chip-body";

    const name = document.createElement("div");
    name.className = "portal-uploader-chip-name";
    name.textContent = upload.file.name;

    const meta = document.createElement("div");
    meta.className = "portal-uploader-chip-meta";

    const progressWrap = document.createElement("div");
    progressWrap.className = "portal-uploader-chip-progress";
    const progressBar = document.createElement("div");
    progressBar.className = "portal-uploader-chip-progress-bar";
    progressWrap.appendChild(progressBar);

    body.appendChild(name);
    body.appendChild(meta);
    body.appendChild(progressWrap);

    const remove = document.createElement("button");
    remove.type = "button";
    remove.className = "portal-uploader-chip-remove";
    remove.setAttribute("aria-label", "Remove");
    remove.dataset.action = "click->portal-document-upload#remove";
    remove.dataset.uploadId = upload.id;
    remove.innerHTML = '<i class="fa fa-times"></i>';

    chip.appendChild(icon);
    chip.appendChild(body);
    chip.appendChild(remove);
    this.fileListTarget.appendChild(chip);

    upload.chipEl = chip;
    upload.metaEl = meta;
    upload.progressBarEl = progressBar;
    upload.progressWrapEl = progressWrap;

    this.updateChip(upload);
  }

  updateChip(upload) {
    if (!upload.chipEl) return;
    const sizeText = this.humanSize(upload.file.size);

    upload.chipEl.classList.remove("is-uploading", "is-complete", "is-error");
    upload.chipEl.classList.add(`is-${upload.status}`);

    if (upload.status === "uploading") {
      upload.metaEl.textContent = `Uploading… ${Math.round(upload.progress)}%`;
      upload.progressWrapEl.style.display = "block";
      upload.progressBarEl.style.width = `${upload.progress}%`;
    } else if (upload.status === "complete") {
      upload.metaEl.innerHTML = `${sizeText} <span class="portal-uploader-chip-status"><i class="fa fa-check-circle"></i> Ready</span>`;
      upload.progressWrapEl.style.display = "none";
    } else if (upload.status === "error") {
      upload.metaEl.innerHTML = `<span class="portal-uploader-chip-status is-error"><i class="fa fa-exclamation-triangle"></i> Upload failed</span>`;
      upload.progressWrapEl.style.display = "none";
    }
  }

  refreshSubmitState() {
    const hasUploading = this.uploads.some((u) => u.status === "uploading");
    const hasComplete = this.uploads.some((u) => u.status === "complete");
    this.submitTarget.disabled = hasUploading || !hasComplete;
  }

  iconFor(file) {
    const type = file.type || "";
    if (type === "application/pdf") return "file-pdf";
    if (type.startsWith("image/")) return "file-image";
    if (type.includes("word")) return "file-word";
    if (type.includes("excel") || type.includes("spreadsheet")) return "file-excel";
    return "file-alt";
  }

  humanSize(bytes) {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 ** 2) return `${(bytes / 1024).toFixed(1)} KB`;
    if (bytes < 1024 ** 3) return `${(bytes / 1024 ** 2).toFixed(1)} MB`;
    return `${(bytes / 1024 ** 3).toFixed(1)} GB`;
  }
}
